import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Upload } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const ROLES = ["Coach", "Team Parent", "Official", "Volunteer", "Other"];

export default function Nominate() {
  const [f, setF] = useState({
    nominee: "", nominee_role: "Coach", sport: "", city_state: "", story: "",
    nominator_name: "", nominator_contact: "", consent_ack: false, subscribe_nominator: false,
  });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
  const onPhoto = (file) => {
    if (!file) { setPhoto(null); return; }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const okType = ALLOWED.includes((file.type || "").toLowerCase()) || ["jpg", "jpeg", "png", "webp", "heic", "heif"].includes(ext);
    if (!okType) { toast.error("Please choose a JPG, PNG, WEBP, or HEIC image."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("That image is over 10MB — please pick a smaller photo."); return; }
    setPhoto(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent_ack) { toast.error("Please confirm you have permission to share this."); return; }
    if (busy) return;
    setBusy(true);
    try {
      let photo_media_id = null;
      if (photo) {
        const fd = new FormData(); fd.append("file", photo);
        const up = await api.post("/ring/nominate/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_media_id = up.data.photo_media_id;
      }
      await api.post("/ring/nominate", { ...f, photo_media_id });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const field = "mt-1 w-full border-2 border-[color:var(--vt-dark)] bg-[#FBF9F5] text-[color:var(--vt-body-on-cream)] px-4 py-3 focus:outline-none focus:border-[color:var(--vt-amber)] transition-colors";
  const label = "vt-eyebrow vt-eyebrow--cream";

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-20 text-center" data-testid="nominate-success">
        <div className="mx-auto w-14 h-14 flex items-center justify-center bg-[color:var(--vt-amber)] text-[color:var(--vt-dark)]"><Check size={28} strokeWidth={3} /></div>
        <h1 className="mt-6 font-head text-[color:var(--vt-body-on-cream)] text-4xl">NOMINATION RECEIVED.</h1>
        <p className="mt-3 text-[color:var(--vt-body-on-cream)] leading-relaxed">Thank you for noticing them. If they're featured, we'll reach out to you privately using the contact info you provided — we never contact the nominee first.</p>
      </div>
    );
  }

  return (
    <div className="vt-paper min-h-screen">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-12 sm:py-16" data-testid="nominate-page">
        <Seo title="Nominate — The Voluntold Ring" description="Nominate the coach, team parent, official, or volunteer who keeps showing up. Tell us their story — we'll tell everyone else." path="/nominate" />
        <span className="vt-eyebrow vt-eyebrow--cream">Recognition</span>
        <img src="/brand/logos/voluntoldhq-shield-on-light.svg" alt="VoluntoldHQ" data-testid="nominate-shield" className="h-[88px] sm:h-[120px] w-auto mb-4" />
        <h1 className="mt-3 font-head text-[color:var(--vt-body-on-cream)] text-5xl sm:text-6xl leading-[0.9]">NOMINATE SOMEONE WHO GOT VOLUNTOLD</h1>
        <p className="mt-5 text-[color:var(--vt-body-on-cream)] text-lg leading-relaxed">The coach, team parent, ref, or volunteer who keeps showing up. Tell us their story — we'll tell everyone else.</p>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <div>
            <label className={label}>Their name</label>
            <input required value={f.nominee} onChange={(e) => set("nominee", e.target.value)} data-testid="nominate-nominee" className={field} placeholder="e.g. Maria Reyes" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Their role</label>
              <select value={f.nominee_role} onChange={(e) => set("nominee_role", e.target.value)} data-testid="nominate-role" className={field}>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Sport</label>
              <input value={f.sport} onChange={(e) => set("sport", e.target.value)} data-testid="nominate-sport" className={field} placeholder="e.g. Soccer" />
            </div>
          </div>
          <div>
            <label className={label}>Town / State</label>
            <input value={f.city_state} onChange={(e) => set("city_state", e.target.value)} data-testid="nominate-location" className={field} placeholder="e.g. Westside, OH" />
          </div>
          <div>
            <label className={label}>Why they deserve it</label>
            <textarea required rows={5} value={f.story} onChange={(e) => set("story", e.target.value)} data-testid="nominate-story" className={field} placeholder="One specific moment beats general praise." />
          </div>
          <div>
            <label className={label}>Optional: upload a photo</label>
            <label className="mt-1 flex items-center gap-2 border-2 border-dashed border-[color:var(--vt-dark)] bg-[#FBF9F5] px-4 py-3 cursor-pointer hover:border-[color:var(--vt-amber)] transition-colors">
              <Upload size={16} className="text-[color:var(--vt-body-on-cream)]" />
              <span className="text-sm text-[color:var(--vt-body-on-cream)]">{photo ? photo.name : "Choose an image (optional)"}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" data-testid="nominate-photo" onChange={(e) => onPhoto(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Your name</label>
              <input required value={f.nominator_name} onChange={(e) => set("nominator_name", e.target.value)} data-testid="nominate-your-name" className={field} />
            </div>
            <div>
              <label className={label}>Your email</label>
              <input required type="email" value={f.nominator_contact} onChange={(e) => set("nominator_contact", e.target.value)} data-testid="nominate-your-contact" className={field} />
            </div>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={f.consent_ack} onChange={(e) => set("consent_ack", e.target.checked)} data-testid="nominate-consent" className="mt-1 w-4 h-4 accent-[color:var(--vt-amber)]" />
            <span className="text-sm text-[color:var(--vt-body-on-cream)] leading-relaxed">I have permission to share this photo and story.</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={f.subscribe_nominator} onChange={(e) => set("subscribe_nominator", e.target.checked)} data-testid="nominate-subscribe" className="mt-1 w-4 h-4 accent-[color:var(--vt-amber)]" />
            <span className="text-sm text-[color:var(--vt-body-on-cream)] leading-relaxed">Also send me The Sideline — one worthwhile read a week.</span>
          </label>
          <button type="submit" disabled={busy} data-testid="nominate-submit" className="vt-btn vt-btn-secondary-cream w-full sm:w-auto">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit Nomination <span className="vt-arrow">→</span></>}
          </button>
        </form>
      </div>
    </div>
  );
}
